// SampleDetail.js

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faInbox, faTruck, faBoxOpen, faTimes, faMapMarkerAlt,
    faPrint, faChevronDown, faSave // Re-added Save icon
} from '@fortawesome/free-solid-svg-icons';
import './SampleDetail.css';

const SampleDetail = ({ order, onClose, onSaveChanges }) => {
    const [editedOrder, setEditedOrder] = useState(order ? JSON.parse(JSON.stringify(order)) : null);
    const [error, setError] = useState(null);
    const [showPrintDropdown, setShowPrintDropdown] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const [isSaving, setIsSaving] = useState(false); // State for saving timeline

    if (!editedOrder) return null;

    const statusIcons = { 'Order Received': faInbox, 'Sent Cargo': faTruck, 'Cargo Delivered': faBoxOpen };
    
    // This now only updates the local state
    const handleDateChange = (stage, dateValue) => {
        setEditedOrder(prev => ({
            ...prev,
            status: { ...prev.status, stages: { ...prev.status.stages, [stage]: dateValue ? new Date(dateValue).toISOString() : null } }
        }));
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toISOString().slice(0, 16);
    };

    // Manual save function for timeline changes
    const handleSaveTimeline = async () => {
        setIsSaving(true);
        setError(null);
        try {
            const response = await fetch(`https://service9004.szapfs.org/api/orders/${editedOrder.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    order_received: editedOrder.status.stages['Order Received'],
                    sent_cargo: editedOrder.status.stages['Sent Cargo'],
                    cargo_delivered: editedOrder.status.stages['Cargo Delivered'],
                }),
            });
            if (!response.ok) throw new Error('Failed to save timeline');
            if (onSaveChanges) onSaveChanges(); // Refresh parent data
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handlePrintRequest = async (printType) => {
        setIsPrinting(true);
        setError(null);
        try {
            const response = await fetch('https://service9004.szapfs.org/api/print', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    orderId: editedOrder.id, items: editedOrder.items, type: printType,
                }),
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.details || 'Failed to print labels');
            
            if (result.labelUrls && result.labelUrls.length > 0) {
                result.labelUrls.forEach(url => window.open(url, '_blank'));
            }
            if (onSaveChanges) onSaveChanges(); // Refresh data to get new URLs
            setShowPrintDropdown(false);
        } catch (err) {
            setError('Print Error: ' + err.message);
        } finally {
            setIsPrinting(false);
        }
    };

    const modalContent = (
        <div className="samplemanager-detail-overlay">
            <div className="samplemanager-detail-modal">
                <div className="samplemanager-detail-header">
                    <div className="samplemanager-detail-icon">
                        <FontAwesomeIcon icon={statusIcons[editedOrder.status.currentStage] || faInbox} />
                    </div>
                    <div className="samplemanager-detail-title">
                        <h3>Order Details</h3>
                        <p className="samplemanager-detail-status">{editedOrder.status.currentStage}</p>
                    </div>
                    <button className="samplemanager-detail-close" onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
                </div>
                <div className="samplemanager-detail-body">
                    {error && <div className="samplemanager-detail-error">{error}</div>}
                    <div className="samplemanager-detail-content">
                        <div className="samplemanager-detail-flex-container">
                            <div className="samplemanager-detail-left-column">
                                <div className="samplemanager-detail-order-id">{editedOrder.id}</div>
                                <div className="samplemanager-detail-section">
                                    <h4>Customer & Shipping</h4>
                                    <div className="samplemanager-detail-grid">
                                        <div className="samplemanager-detail-item-container"> 
                                            <div className="samplemanager-detail-item">
                                                <div className="samplemanager-detail-item-label">Name</div>
                                                <div className="samplemanager-detail-item-address">{editedOrder.firstName} {editedOrder.lastName}</div>
                                            </div>
                                            <div className="samplemanager-detail-item">
                                                <div className="samplemanager-detail-item-label">Email</div>
                                                <div className="samplemanager-detail-item-address">{editedOrder.email || 'N/A'}</div>
                                            </div>
                                        </div>
                                        <div className="samplemanager-detail-item">
                                            <div className="samplemanager-detail-item-label"><FontAwesomeIcon icon={faMapMarkerAlt} /> Shipping Address</div>
                                            <div className="samplemanager-detail-item-address">{editedOrder.address || 'No address provided.'}</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="samplemanager-detail-section">
                                    <h4>Kits in this Order</h4>
                                    <div className="samplemanager-detail-kit-grid">
                                        {editedOrder.items.map(item => (
                                            <div key={item.KitID} className="samplemanager-detail-kit-card">
                                                <img src={item.ImageURL} alt={item.KitName} className="samplemanager-detail-kit-image" />
                                                <div className="samplemanager-detail-kit-name">{item.KitName}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="samplemanager-detail-print-section">
                                    <div className="samplemanager-detail-print-buttons">
                                        <div className="samplemanager-detail-print-left">
                                            <button className="samplemanager-detail-print-all" onClick={() => handlePrintRequest('all')} disabled={isPrinting}>
                                                <FontAwesomeIcon icon={faPrint} /> {isPrinting ? 'Printing...' : 'Print All'}
                                            </button>
                                            <div className="samplemanager-detail-print-details">
                                                1 Shipping, {editedOrder.items.length} Return Labels
                                            </div>
                                        </div>
                                        <div className="samplemanager-detail-print-right">
                                            <div className="samplemanager-detail-dropdown">
                                                <button className="samplemanager-detail-dropdown-button" onClick={() => setShowPrintDropdown(!showPrintDropdown)} disabled={isPrinting}>
                                                    <FontAwesomeIcon icon={faChevronDown} />
                                                </button>
                                                {showPrintDropdown && (
                                                    <div className="samplemanager-detail-dropdown-menu">
                                                        <button onClick={() => handlePrintRequest('shipping')} disabled={isPrinting}>Print Shipping Label</button>
                                                        <button onClick={() => handlePrintRequest('return')} disabled={isPrinting}>Print Return Labels</button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="samplemanager-detail-right-column">
                                <div className="samplemanager-detail-section">
                                    <h4>Status Timeline</h4>
                                    <div className="samplemanager-detail-timeline">
                                        {Object.keys(statusIcons).map((stage, index) => (
                                            <div key={stage} className={`samplemanager-detail-timeline-item ${editedOrder.status.stages[stage] ? 'completed' : ''}`}>
                                                <div className="samplemanager-detail-timeline-connector">
                                                    <div className="samplemanager-detail-timeline-dot"></div>
                                                    {index < Object.keys(statusIcons).length - 1 && <div className="samplemanager-detail-timeline-line"></div>}
                                                </div>
                                                <div className="samplemanager-detail-timeline-content">
                                                    <FontAwesomeIcon icon={statusIcons[stage]} className="samplemanager-detail-stage-icon"/>
                                                    <span className="samplemanager-detail-stage-name">{stage}</span>
                                                    <div className="samplemanager-detail-stage-date">
                                                        <input type="datetime-local" value={formatDateForInput(editedOrder.status.stages[stage])} onChange={(e) => handleDateChange(stage, e.target.value)} className="samplemanager-detail-date-input" />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="samplemanager-detail-timeline-save">
                                        <button onClick={handleSaveTimeline} disabled={isSaving}>
                                            <FontAwesomeIcon icon={faSave} /> {isSaving ? 'Saving...' : 'Save Timeline'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
    
    return createPortal(modalContent, document.body);
};

export default SampleDetail;